import * as Rx from 'rxjs';
import * as URI from 'urijs';
import * as $script from 'loadjs';
// types
import {
    IUbiConnectConfig,
    IConnect,
    IWindowWithConnect,
    IGeneralUserData,
    ICallback,
    ILogoutResponse,
    IGetTicketResponse,
    IGetProfilesResponse,
    IGetUserStatusResponse,
    IGetThirdPartyStatusResponse,
    ITriggerEmailActivationResponse
} from './types';
// constants
import { externalLoginOptionType } from './constants';
// utils
import { calculatePopupSize, getConnectEndpoint, getSDKEndpoint, TPC_POPUP_NEXT_URL } from './utils';

interface ITicket {
    readonly subscribe: Function;
}

interface IConnectSDK {
    readonly getTicket: () => ITicket;
    readonly getTicketFromSsoId: Function;
    readonly getProfiles: Function;
    readonly logout: Function;
    readonly getUserStatus: Function;
    readonly getThirdPartyStatus: Function;
    readonly triggerEmailActivation: Function;
}

interface IUbisoftConnectService {
    readonly init: (config: IUbiConnectConfig) => Promise<void>;
    readonly getTicket: () => Promise<{ ticket: string, sessionId: string, userId: string } | void>;
    readonly getLoginPopIn: (externalLogin: string, languageCode?: string, size?: { width: string, height: string }) => HTMLIFrameElement;
    readonly openLoginPopUp: (languageCode: string, externalLogin: externalLoginOptionType, align?: string) => void;
    readonly logout: (ticket: string, sessionId: string) => void;
    readonly getProfiles: (generalUserData: IGeneralUserData) => void;
    readonly getUserStatus: (generalUserData: IGeneralUserData, callback: ICallback<IGetUserStatusResponse>) => void;
    readonly getThirdPartyStatus: () => Promise<boolean>;
    readonly triggerEmailActivation:
    (generalUserData: IGeneralUserData, callback: ICallback<ITriggerEmailActivationResponse>) => void;
}

export class UbisoftConnectService implements IUbisoftConnectService {
    private config: IUbiConnectConfig;
    private connectSDK: IConnectSDK | null;
    private loginCallback: () => void;
    private sdkSubscription: {
        readonly unsubscribe: Function
    } | null;
    private static hasEmbeddedModeEnabled: boolean;
    private static websiteAbsolutePath: string;
    private ssoTicket: { ticket: string, sessionId: string, userId: string } | null;

    public sizeSubject: Rx.Subject<number> = new Rx.Subject();

    private isLoginMessage(event: MessageEvent): boolean {
        const isMessageLocationBaseCorrect = UbisoftConnectService.hasEmbeddedModeEnabled
            || event.origin === window.location.origin
            || event.origin === getConnectEndpoint(this.config.envName);
        const isCorrectGenomeId = event.data.genomeId === this.config.genomeId;
        const isCorrectTopic = event.data.topic === this.config.topicOfPostMessage;
        const isCorrectApplicationId = event.data.applicationId === this.config.applicationId;

        return isMessageLocationBaseCorrect && isCorrectGenomeId && isCorrectTopic && isCorrectApplicationId;
    }

    private isSSOTicketMessage(event: MessageEvent): boolean {
        return event.data.successSso;
    }

    private isSizeChangedMessage(event: MessageEvent): number | false {
        let data: { connectRoute: string, windowHeight: number };

        try {
            data = JSON.parse(event.data);
        } catch (error) {
            return false;
        }

        return (data.connectRoute && data.windowHeight) ? data.windowHeight : false;
    }

    private receiveUbisoftConnectIframeMessage = (event: MessageEvent) => {
        const isLoginMessage = this.isLoginMessage(event);
        const isSSOTicketMessage = this.isSSOTicketMessage(event);
        const isSizeChangedMessage = this.isSizeChangedMessage(event);

        if (this.config) {
            if (this.loginCallback && isLoginMessage) {
                this.loginCallback();
            } else if (isSizeChangedMessage) {
                this.sizeSubject.next(isSizeChangedMessage);
            } else if (isSSOTicketMessage) {
                const { ticket, sessionId, userId } = event.data;
                this.ssoTicket = { ticket, sessionId, userId };
                this.loginCallback();
            }
        }
    };

    private subscriber = (callback: () => void) => (sdk: IConnectSDK | null) => {
        this.connectSDK = sdk;
        this.sdkSubscription && this.sdkSubscription.unsubscribe();
        callback();
    };

    private prepareConfig = (config: IUbiConnectConfig): IUbiConnectConfig => {
        return {
            ...config,
            nextUrl: this.createNextURL(config.nextUrl)
        };
    };

    private createNextURL = (nextUrl: string): string => {
        return new URI(UbisoftConnectService.websiteAbsolutePath).filename(nextUrl).href();
    };

    private generateConnectNextUrl = (hasTPCEnabled: boolean) => {
        return hasTPCEnabled
            ? this.config.nextUrl
            : TPC_POPUP_NEXT_URL(
                UbisoftConnectService.websiteAbsolutePath,
                this.config.applicationId,
                this.config.genomeId,
                this.config.envName
            );
    };

    public static configure = (options: { hasEmbeddedModeEnabled: boolean, websiteAbsolutePath: string }): void => {
        UbisoftConnectService.hasEmbeddedModeEnabled = options.hasEmbeddedModeEnabled;
        UbisoftConnectService.websiteAbsolutePath = options.websiteAbsolutePath;
    };

    public init(config: IUbiConnectConfig) {
        this.config = this.prepareConfig(config);
        /**
         * TODO: temporary solution, waiting for ConnectSDK multiple instances feature.
         * During mounting and unmounting of application we work with the same instance of UbisoftConnectService,
         * because of this fact, we should clear sdkSubscription each calling of init method
         */
        this.connectSDK = null;
        this.sdkSubscription = null;

        window.addEventListener('message', this.receiveUbisoftConnectIframeMessage, false);

        return new Promise<void>( (resolve) => {
            const subscriber = this.subscriber(resolve);
            const Connect = (window as IWindowWithConnect).Connect;
            const { applicationId, genomeId, lang, nextUrl, envName } = this.config;

            /**
             * TODO: temporary solution, waiting for ConnectSDK multiple instances feature.
             * 1) Try to subscribe to sdk in case website was loaded on the page which already has Connect instance.
             * We should do it before "init" method calling because "init" invoke emitting new value to the Subject
             * to which we subscribe, so in case we subscribe after "init" our subscriber will be never called.
             * 2) We should check if is Connect.sdk object exist because this object is creating inside "init" method logic.
             */
            if (!this.sdkSubscription && Connect.sdk) {
                this.sdkSubscription = Connect.sdk.subscribe(subscriber);
            }

            Connect.init({
                env: envName,
                appId: applicationId,
                genomeId: genomeId,
                lang: lang,
                nextUrl: encodeURIComponent(nextUrl),
                thirdPartyCookiesSupport: !!this.config.thirdPartyCookiesSupport
            });

            if (!this.sdkSubscription && Connect.sdk) {
                this.sdkSubscription = Connect.sdk.subscribe(subscriber);
            }
        });
    }

    public registerLoginCallback(loginCallback: () => void) {
        this.loginCallback = loginCallback;
    }

    public static loadSDKScript(envName: string) {
        return new Promise<IConnect>(resolve => {
            if ((window as IWindowWithConnect).Connect) {
                return resolve();
            }

            const scriptId = 'connectSDK';
            const scriptUrl = getSDKEndpoint(envName);

            $script(scriptUrl, scriptId);
            $script.ready(scriptId, function () {
                resolve();
            });
        });
    }

    public getTicket(hasTPCEnabled: boolean = true) {
        return new Promise<{
            ticket: string,
            sessionId: string,
            userId: string
        }>((resolve, reject) => {
            if (!hasTPCEnabled && this.ssoTicket) {
                return resolve(this.ssoTicket);
            }
            let gotTicket = false;
            const timeoutId = setTimeout(function() {
                if (!gotTicket) {
                    console.warn('[Ubisoft Connect] Get ticket timeout.');
                    reject();
                }
            }, 10 * 1000);

            this.connectSDK &&
            this.connectSDK.getTicket().subscribe((getTicketResponse: IGetTicketResponse) => {
                gotTicket = true;
                clearTimeout(timeoutId);

                if (getTicketResponse.status !== 'ok') {
                    return reject();
                }
                const { payload: { ticket, userId, sessionId } } = getTicketResponse;
                return resolve({ ticket, userId, sessionId });
            });
        });
    }

    public getThirdPartyStatus() {
        return new Promise<boolean>(resolve => {
            this.connectSDK && this.connectSDK.getThirdPartyStatus()
                .subscribe((getThirdPartyStatusResponse: IGetThirdPartyStatusResponse) => {
                    resolve(getThirdPartyStatusResponse.status === 'ok');
                });
        });
    }

    public getProfiles({ ticket, sessionId, userId }: IGeneralUserData) {
        return new Promise<IGetProfilesResponse>(resolve => {
            this.connectSDK &&
            this.connectSDK.getProfiles(ticket, sessionId, userId).subscribe((getProfilesResponse: IGetProfilesResponse) => {
                resolve(getProfilesResponse);
            });
        });
    }

    public getLoginPopIn = (externalLogin: string, languageCode?: string, size?: { width?: string, height?: string }) => {
        const iframe: HTMLIFrameElement = document.createElement('iframe');

        iframe.sandbox.add(
            'allow-same-origin',
            'allow-forms',
            'allow-popups-to-escape-sandbox',
            'allow-popups',
            'allow-scripts');

        const url = new URI(getConnectEndpoint(this.config.envName))
            .segment([
                externalLogin
            ])
            .query({
                appId: this.config.applicationId,
                genomeId: this.config.genomeId,
                lang: languageCode || this.config.lang,
                nextUrl: decodeURIComponent(this.config.nextUrl)
            })
            .href();

        iframe.setAttribute('src', url);

        iframe.classList.add('iFrame');
        iframe.setAttribute('width', size && size.width ? size.width : '480');
        iframe.setAttribute('height', size && size.height ? size.height : '720');
        return iframe;
    };

    public openLoginPopUp(
        languageCode: string,
        externalLogin: externalLoginOptionType,
        align?: string,
        hasTPCEnabled: boolean = true
    ) {
        const connectNextUrl: string = this.generateConnectNextUrl(hasTPCEnabled);
        const url = new URL(`${getConnectEndpoint(this.config.envName)}/${externalLogin}`);
        url.searchParams.set('appId', this.config.applicationId);
        url.searchParams.set('genomeId', this.config.genomeId);
        url.searchParams.set('lang', languageCode || this.config.lang);
        url.searchParams.set('nextUrl', connectNextUrl);

        const target = 'Ubisoft Connect';
        const popupSize = calculatePopupSize(align);
        const features = `width=${popupSize.width},height=${popupSize.height},left=${popupSize.left},top=${
            popupSize.top
        },toolbar=no,status=no`;

        return window.open(url.toString(), target, features);
    }

    public logout(ticket: string, sessionId: string) {
        return new Promise<void>((resolve, reject) => {
            this.connectSDK &&
            this.connectSDK.logout(ticket, sessionId).subscribe((logoutResponse: ILogoutResponse) => {
                logoutResponse.status === 'ok' ? resolve() : reject();
            });
        });
    }

    public getUserStatus(
        { ticket, sessionId, userId }: IGeneralUserData,
        callback: (getUserStatusResponse: IGetUserStatusResponse) => void
    ) {
        this.connectSDK &&
        this.connectSDK.getUserStatus(ticket, sessionId, userId).subscribe(callback);
    }

    public triggerEmailActivation(
        { ticket, sessionId, userId }: IGeneralUserData,
        callback: (triggerEmailActivationResponse: ITriggerEmailActivationResponse) => void
    ) {
        this.connectSDK &&
        this.connectSDK.triggerEmailActivation(ticket, sessionId, userId).subscribe(callback);
    }
}

const ubisoftConnectService = new UbisoftConnectService();

export default ubisoftConnectService;
